import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useLazyCommand, useEvent } from '@soap/modules';
import { ChevronLeft, ChevronRight, Plus, Trash2, X, Image as ImageIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { secondsToMMSS, mmssToSeconds } from '../utils/timeFormat';
import { ASSESSMENT_TIMING_DEFAULTS } from '../../z50-domaincomp/constants/assessment-timing-defaults';
import { timingModeKeys } from './EditAssessment2/types';
import { ImageUpload } from '../ui/image-upload';

// Default time per question in seconds (centralized constant)
const DEFAULT_SECONDS_PER_QUESTION = ASSESSMENT_TIMING_DEFAULTS.DEFAULT_SECONDS_PER_QUESTION;

interface QuestionWizardProps {
  open: boolean;
  onClose: () => void;
  assessmentId: string;
  sectionId: string;
  onQuestionAdded?: () => void;
  editingQuestionId?: string;
  timingMode?: string; // Assessment timing mode (e.g., "not-timed", "per-question", "per-section", "per-assessment")
  sectionTimeLimit?: number;
  sectionQuestionCount?: number;
  overallTimeLimit?: number;
  totalQuestionCount?: number;
  initialQuestionData?: {
    questionText: string;
    questionType: QuestionType;
    points: number;
    maximumValue?: number;
    timeLimit: number | null;
    options?: QuestionOption[];
    charLimit?: number;
    allowPartialCredit?: boolean;
    mediaItem?: { id: string; name: string } | null;
    mediaCaption?: string;
  };
}

type QuestionType = 'multiple-choice' | 'boolean' | 'freetext' | 'single-selection' | 'specific-string';

interface QuestionOption {
  key: string;
  value: string;
  isCorrect: boolean;
}

const questionTypes: { key: QuestionType; label: string }[] = [
  { key: 'multiple-choice', label: 'Multiple Choice' },
  { key: 'boolean', label: 'Boolean (True/False)' },
  { key: 'freetext', label: 'Free Text' },
  { key: 'single-selection', label: 'Single Selection' },
  { key: 'specific-string', label: 'Specific String' }
];

const viewStates = {
  ready: 0,
  adding: 1,
  updatingTime: 2,
  complete: 3
};

export function QuestionWizard({ open, onClose, assessmentId, sectionId, onQuestionAdded, editingQuestionId, timingMode, sectionTimeLimit, sectionQuestionCount, overallTimeLimit, totalQuestionCount, initialQuestionData }: QuestionWizardProps) {
  // Use default (30 seconds) if timeLimit is 0 or null when timing mode is per-question
  const getEffectiveTimeLimit = React.useCallback((value: number | null | undefined): number | null => {
    if (timingMode === 'per-question' && (value === null || value === undefined || value === 0)) {
      return DEFAULT_SECONDS_PER_QUESTION;
    }
    return value ?? null;
  }, [timingMode]);
  
  const effectiveTimeLimit = getEffectiveTimeLimit(initialQuestionData?.timeLimit);
  
  const [step, setStep] = useState(1);
  const [viewState, setViewState] = useState(viewStates.ready);
  const [questionText, setQuestionText] = useState(initialQuestionData?.questionText || '');
  const [questionType, setQuestionType] = useState<QuestionType | ''>(initialQuestionData?.questionType || '');
  const [points, setPoints] = useState(initialQuestionData?.points || initialQuestionData?.maximumValue || 10);
  const [timeLimit, setTimeLimit] = useState<number | null>(effectiveTimeLimit);
  const [timeLimitDisplay, setTimeLimitDisplay] = useState<string>(secondsToMMSS(effectiveTimeLimit));
  const [options, setOptions] = useState<QuestionOption[]>(initialQuestionData?.options || []);
  const [charLimit, setCharLimit] = useState(initialQuestionData?.charLimit || 500);
  const [allowPartialCredit, setAllowPartialCredit] = useState(initialQuestionData?.allowPartialCredit || false);
  const [mediaItem, setMediaItem] = useState<any>(null);
  const [mediaCaption, setMediaCaption] = useState('');
  
  const [addQuestion] = useLazyCommand('C290v1_AddEmbeddedQuestionToAssessment');
  const [editQuestion] = useLazyCommand('C279v1_EditEmbeddedQuestion');
  const [updateTimings] = useLazyCommand('C195v1_UpdateAssessmentTimeLimits');
  
  const isPerSection = timingMode === timingModeKeys.PerSection;
  const isPerAssessment = timingMode === timingModeKeys.PerAssessment;
  const shouldShowTimeAdjustment = !editingQuestionId && (isPerSection || isPerAssessment) && 
    ((isPerSection && sectionTimeLimit !== undefined && sectionQuestionCount !== undefined) ||
     (isPerAssessment && overallTimeLimit !== undefined && totalQuestionCount !== undefined));
  
  // Time adjustment state (only for adding questions, not editing) - checked by default
  const [adjustTime, setAdjustTime] = useState(shouldShowTimeAdjustment);
  const [newTimeDisplay, setNewTimeDisplay] = useState('');
  const [pendingTimeUpdate, setPendingTimeUpdate] = useState<{ newTime: number; isPerSection: boolean; sectionId?: string } | null>(null);
  const [addedQuestionId, setAddedQuestionId] = useState<string | null>(null);
  
  // Calculate new time display for time adjustment step
  useEffect(() => {
    if (shouldShowTimeAdjustment && step === 3) {
      if (isPerSection && sectionTimeLimit !== undefined && sectionQuestionCount !== undefined) {
        const currentTime = sectionTimeLimit || 0;
        if (sectionQuestionCount === 0) {
          // First question - new time is just the default time per question
          setNewTimeDisplay(secondsToMMSS(DEFAULT_SECONDS_PER_QUESTION));
        } else {
          // Calculate: current time + (current time / number of questions)
          const timePerQuestion = currentTime / sectionQuestionCount;
          const newTime = currentTime + timePerQuestion;
          setNewTimeDisplay(secondsToMMSS(Math.max(0, Math.floor(newTime))));
        }
      } else if (isPerAssessment && overallTimeLimit !== undefined && totalQuestionCount !== undefined) {
        const currentTime = overallTimeLimit || 0;
        if (totalQuestionCount === 0) {
          // First question - new time is just the default time per question
          setNewTimeDisplay(secondsToMMSS(DEFAULT_SECONDS_PER_QUESTION));
        } else {
          // Calculate: current time + (current time / total number of questions)
          const timePerQuestion = currentTime / totalQuestionCount;
          const newTime = currentTime + timePerQuestion;
          setNewTimeDisplay(secondsToMMSS(Math.max(0, Math.floor(newTime))));
        }
      }
    }
  }, [shouldShowTimeAdjustment, step, isPerSection, isPerAssessment, sectionTimeLimit, sectionQuestionCount, overallTimeLimit, totalQuestionCount]);

  // Listen for question added event, then update time if needed
  useEvent({
    eventName: "E266v1_EmbeddedQuestionAddedToAssessment",
    onEventReceived: (event: any) => {
      const eventAssessmentId = event.e266_AssessmentId || event.E266_AssessmentId;
      const eventSectionId = event.e266_SectionId || event.E266_SectionId;
      const eventQuestionId = event.e266_QuestionId || event.E266_QuestionId;
      
      if (eventAssessmentId === assessmentId && 
          eventSectionId === sectionId && 
          viewState === viewStates.adding) {
        setAddedQuestionId(eventQuestionId);
        
        // Question was added, now update time if needed
        if (pendingTimeUpdate) {
          setViewState(viewStates.updatingTime);
          if (pendingTimeUpdate.isPerSection && pendingTimeUpdate.sectionId) {
            updateTimings({
              $type: "C195v1_UpdateAssessmentTimeLimits",
              C195_AssessmentId: assessmentId,
              C195_AssessmentTimingModeKey: timingModeKeys.PerSection,
              C195_SectionId: pendingTimeUpdate.sectionId,
              C195_TimeInSeconds: pendingTimeUpdate.newTime,
            });
          } else if (!pendingTimeUpdate.isPerSection) {
            updateTimings({
              $type: "C195v1_UpdateAssessmentTimeLimits",
              C195_AssessmentId: assessmentId,
              C195_AssessmentTimingModeKey: timingModeKeys.PerAssessment,
              C195_TimeInSeconds: pendingTimeUpdate.newTime,
            });
          }
        } else {
          // No time update needed, close immediately
          setViewState(viewStates.complete);
          resetWizard();
          onClose();
          if (onQuestionAdded) {
            onQuestionAdded();
          }
        }
      }
    }
  }, [assessmentId, sectionId, viewState, pendingTimeUpdate, updateTimings, onQuestionAdded, onClose]);

  // Listen for timing update event (fired after time limit update)
  useEvent({
    eventName: "E268v1_AssessmentTimingUpdated",
    onEventReceived: (event: any) => {
      const eventAssessmentId = event.e268_AssessmentId || event.E268_AssessmentId;
      
      // Filter by assessment ID and check if we're waiting for a time update
      if (eventAssessmentId === assessmentId && 
          viewState === viewStates.updatingTime && 
          pendingTimeUpdate) {
        
        // Time update is complete!
        setPendingTimeUpdate(null);
        setViewState(viewStates.complete);
        resetWizard();
        onClose();
        if (onQuestionAdded) {
          onQuestionAdded();
        }
      }
    }
  }, [assessmentId, viewState, pendingTimeUpdate, onQuestionAdded, onClose]);

  // Reset form when initialQuestionData changes (for edit mode)
  React.useEffect(() => {
    if (initialQuestionData) {
      setQuestionText(initialQuestionData.questionText);
      setQuestionType(initialQuestionData.questionType);
      setPoints(initialQuestionData.points || initialQuestionData.maximumValue || 10);
      const effective = getEffectiveTimeLimit(initialQuestionData.timeLimit);
      setTimeLimit(effective);
      setTimeLimitDisplay(secondsToMMSS(effective));
      setOptions(initialQuestionData.options || []);
      setCharLimit(initialQuestionData.charLimit || 500);
      setAllowPartialCredit(initialQuestionData.allowPartialCredit || false);
      setMediaItem(initialQuestionData.mediaItem || null);
      setMediaCaption(initialQuestionData.mediaCaption || '');
      setStep(1);
    }
  }, [initialQuestionData]);

  const resetWizard = React.useCallback(() => {
    const showTimeAdjustment = !editingQuestionId && (isPerSection || isPerAssessment) && 
      ((isPerSection && sectionTimeLimit !== undefined && sectionQuestionCount !== undefined) ||
       (isPerAssessment && overallTimeLimit !== undefined && totalQuestionCount !== undefined));
    setStep(1);
    setViewState(viewStates.ready);
    setAdjustTime(showTimeAdjustment);
    setNewTimeDisplay('');
    setPendingTimeUpdate(null);
    setAddedQuestionId(null);
    if (initialQuestionData) {
      setQuestionText(initialQuestionData.questionText);
      setQuestionType(initialQuestionData.questionType);
      setPoints(initialQuestionData.points || initialQuestionData.maximumValue || 10);
      const effective = getEffectiveTimeLimit(initialQuestionData.timeLimit);
      setTimeLimit(effective);
      setTimeLimitDisplay(secondsToMMSS(effective));
      setOptions(initialQuestionData.options || []);
      setCharLimit(initialQuestionData.charLimit || 500);
      setAllowPartialCredit(initialQuestionData.allowPartialCredit || false);
      setMediaItem(initialQuestionData.mediaItem || null);
      setMediaCaption(initialQuestionData.mediaCaption || '');
    } else {
      setQuestionText('');
      setQuestionType('');
      setPoints(10);
      setTimeLimit(null);
      setTimeLimitDisplay('00:00');
      setOptions([]);
      setCharLimit(500);
      setAllowPartialCredit(false);
      setMediaItem(null);
      setMediaCaption('');
    }
  }, [initialQuestionData, getEffectiveTimeLimit, editingQuestionId, isPerSection, isPerAssessment, sectionTimeLimit, sectionQuestionCount, overallTimeLimit, totalQuestionCount]);

  const handleClose = () => {
    resetWizard();
    onClose();
  };

  const handleNext = () => {
    if (step === 1 && questionText.trim() && questionType) {
      setStep(2);
    } else if (step === 2) {
      // Go to time adjustment step if applicable, otherwise go to review
      if (shouldShowTimeAdjustment) {
        setStep(3);
      } else {
        setStep(4);
      }
    } else if (step === 3) {
      // Time adjustment step -> Review step
      setStep(4);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      // If we're on step 4 (review) and time adjustment step shouldn't be shown,
      // go back to step 2 instead of step 3
      if (step === 4 && !shouldShowTimeAdjustment) {
        setStep(2);
      } else {
        setStep(step - 1);
      }
    }
  };

  const handleAddOption = () => {
    const newKey = `option_${Date.now()}`;
    // For specific-string, all answers must be correct
    const isCorrect = questionType === 'specific-string' ? true : false;
    setOptions(prevOptions => [...prevOptions, { key: newKey, value: '', isCorrect }]);
  };

  const handleRemoveOption = (key: string) => {
    setOptions(prevOptions => {
      const filtered = prevOptions.filter(o => o.key !== key);
      // For specific-string, ensure all remaining answers are still marked as correct
      if (questionType === 'specific-string' && filtered.length > 0) {
        return filtered.map(o => ({ ...o, isCorrect: true }));
      }
      return filtered;
    });
  };

  const handleOptionChange = (key: string, field: 'value' | 'isCorrect', newValue: string | boolean) => {
    if (field === 'isCorrect' && questionType === 'single-selection' && newValue === true) {
      // For single-selection, only one answer can be correct - uncheck all others
      setOptions(options.map(o => 
        o.key === key ? { ...o, isCorrect: true } : { ...o, isCorrect: false }
      ));
    } else {
      setOptions(options.map(o => 
        o.key === key ? { ...o, [field]: newValue } : o
      ));
    }
  };

  const getCalculationText = () => {
    if (isPerSection && sectionTimeLimit !== undefined && sectionQuestionCount !== undefined) {
      const currentTime = sectionTimeLimit || 0;
      if (sectionQuestionCount === 0) {
        return `Adding first question\nTime per question: ${secondsToMMSS(DEFAULT_SECONDS_PER_QUESTION)}\nNew section time: ${secondsToMMSS(DEFAULT_SECONDS_PER_QUESTION)}`;
      } else {
        const timePerQuestion = currentTime / sectionQuestionCount;
        const newTime = currentTime + timePerQuestion;
        return `Current section time: ${secondsToMMSS(currentTime)}\nTime per question: ${secondsToMMSS(Math.floor(timePerQuestion))} (${secondsToMMSS(currentTime)} ÷ ${sectionQuestionCount})\nNew section time: ${secondsToMMSS(Math.max(0, Math.floor(newTime)))} (${secondsToMMSS(currentTime)} + ${secondsToMMSS(Math.floor(timePerQuestion))})`;
      }
    } else if (isPerAssessment && overallTimeLimit !== undefined && totalQuestionCount !== undefined) {
      const currentTime = overallTimeLimit || 0;
      if (totalQuestionCount === 0) {
        return `Adding first question\nTime per question: ${secondsToMMSS(DEFAULT_SECONDS_PER_QUESTION)}\nNew overall time: ${secondsToMMSS(DEFAULT_SECONDS_PER_QUESTION)}`;
      } else {
        const timePerQuestion = currentTime / totalQuestionCount;
        const newTime = currentTime + timePerQuestion;
        return `Current overall time: ${secondsToMMSS(currentTime)}\nTime per question: ${secondsToMMSS(Math.floor(timePerQuestion))} (${secondsToMMSS(currentTime)} ÷ ${totalQuestionCount})\nNew overall time: ${secondsToMMSS(Math.max(0, Math.floor(newTime)))} (${secondsToMMSS(currentTime)} + ${secondsToMMSS(Math.floor(timePerQuestion))})`;
      }
    }
    return '';
  };

  const handleSubmit = async () => {
    // Validate points maximum value
    if (points > 100) {
      console.error('Points cannot exceed 100');
      return;
    }
    
    // Validate time limit is required when timing mode is per-question
    if (timingMode === 'per-question' && (timeLimit === null || timeLimit < 10)) {
      console.error('Time limit is required and must be at least 10 seconds when assessment timing mode is per-question');
      return;
    }
    
    // Ensure we have options for question types that require them
    if ((questionType === 'multiple-choice' || questionType === 'boolean' || questionType === 'single-selection' || questionType === 'specific-string') && (!options || options.length === 0)) {
      console.error('Options are required for this question type');
      return;
    }

    let questionData: any = {};

    if (questionType === 'multiple-choice') {
      questionData = {
        $type: 'atest.Models.ValueTypes.Questions.MultipleChoiceQuestion, atest.Models',
        PossibleAnswers: options.map(o => ({
          Key: o.key,
          Value: o.value
        })),
        CorrectAnswer: {
          Keys: options.filter(o => o.isCorrect).map(o => o.key)
        },
        AllowPartialCredit: allowPartialCredit
      };
    } else if (questionType === 'boolean') {
      questionData = {
        $type: 'atest.Models.ValueTypes.Questions.BooleanQuestion, atest.Models',
        PossibleAnswers: options.map(o => ({
          Key: o.key,
          Value: o.value
        })),
        CorrectAnswer: {
          Key: options.find(o => o.isCorrect)?.key || ''
        }
      };
    } else if (questionType === 'freetext') {
      questionData = {
        $type: 'atest.Models.ValueTypes.Questions.FreeTextQuestion, atest.Models',
        CharLimit: charLimit,
        AllowPartialCredit: allowPartialCredit
      };
    } else if (questionType === 'single-selection') {
      // For single-selection, exactly one answer must be correct
      const correctOption = options.find(o => o.isCorrect);
      if (!correctOption) {
        console.error('Single-selection question must have exactly one correct answer');
        return;
      }
      
      questionData = {
        $type: 'atest.Models.ValueTypes.Questions.SingleSelectionQuestion, atest.Models',
        PossibleAnswers: options.map(o => ({
          Key: o.key,
          Value: o.value
        })),
        CorrectAnswer: {
          Key: correctOption.key
        }
      };
    } else if (questionType === 'specific-string') {
      // For specific-string, all answers must be correct
      // Ensure all options are marked as correct before submission
      const allOptionsCorrect = options.map(o => ({ ...o, isCorrect: true }));
      setOptions(allOptionsCorrect);
      
      questionData = {
        $type: 'atest.Models.ValueTypes.Questions.SpecificStringQuestion, atest.Models',
        PossibleAnswers: allOptionsCorrect.map(o => ({
          Key: o.key,
          Value: o.value
        })),
        CorrectAnswer: {
          Keys: allOptionsCorrect.map(o => o.key)
        }
      };
    }

    // Only include time limit if timing mode is per-question
    const timeLimitToSend = timingMode === 'per-question' ? timeLimit : null;
    
    // If editing, update the question in place (preserving the question ID)
    if (editingQuestionId) {
      await editQuestion({
        C279_AssessmentId: assessmentId,
        C279_QuestionId: editingQuestionId,
        C279_QuestionText: questionText,
        C279_QuestionTypeKey: questionType,
        C279_MaximumValue: points, // Map points to MaximumValue property
        C279_TimeLimitInSeconds: timeLimitToSend,
        C279_QuestionData: JSON.stringify(questionData),
        C279_MediaItem: mediaItem,
        C279_MediaCaption: mediaCaption
      });
      handleClose();
      if (onQuestionAdded) {
        onQuestionAdded();
      }
    } else {
      // Store the time update info if needed
      if (adjustTime && shouldShowTimeAdjustment) {
        const newTimeInSeconds = mmssToSeconds(newTimeDisplay);
        if (newTimeInSeconds !== null && newTimeInSeconds >= 0 && newTimeInSeconds <= 3600) {
          setPendingTimeUpdate({
            newTime: newTimeInSeconds,
            isPerSection: isPerSection,
            sectionId: sectionId
          });
        }
      }

      // Add a new question
      setViewState(viewStates.adding);
      await addQuestion({
        C290_AssessmentId: assessmentId,
        C290_SectionId: sectionId,
        C290_QuestionText: questionText,
        C290_QuestionTypeKey: questionType,
        C290_MaximumValue: points, // Map points to MaximumValue property
        C290_TimeLimitInSeconds: timeLimitToSend,
        C290_QuestionData: JSON.stringify(questionData),
        C290_MediaItem: mediaItem,
        C290_MediaCaption: mediaCaption
      });
      // Don't close yet - wait for event handlers to process
    }
  };

  const canProceed = () => {
    if (step === 1) {
      const hasRequiredFields = questionText.trim() && questionType;
      // If timing mode is per-question, time limit is required
      if (timingMode === 'per-question') {
        return hasRequiredFields && timeLimit !== null && timeLimit >= 10;
      }
      return hasRequiredFields;
    } else if (step === 2) {
      if (questionType === 'multiple-choice') {
        return options.length >= 2 && options.every(o => o.value.trim()) && options.some(o => o.isCorrect);
      } else if (questionType === 'single-selection') {
        // Single-selection must have exactly one correct answer
        const correctCount = options.filter(o => o.isCorrect).length;
        return options.length >= 2 && options.every(o => o.value.trim()) && correctCount === 1;
      } else if (questionType === 'boolean') {
        return options.length === 2 && options.every(o => o.value.trim()) && options.some(o => o.isCorrect);
      } else if (questionType === 'freetext') {
        return true;
      } else if (questionType === 'specific-string') {
        // Specific-string must have all answers marked as correct
        return options.length >= 1 && options.every(o => o.value.trim() && o.isCorrect);
      }
    } else if (step === 3) {
      // Time adjustment step - always can proceed (adjustTime is optional)
      return true;
    }
    return false;
  };

  // Initialize boolean options if needed
  React.useEffect(() => {
    if (questionType === 'boolean' && options.length === 0) {
      setOptions([
        { key: 'true', value: 'True', isCorrect: false },
        { key: 'false', value: 'False', isCorrect: false }
      ]);
    }
  }, [questionType]);

  // For specific-string, ensure all answers are marked as correct when question type changes
  React.useEffect(() => {
    if (questionType === 'specific-string' && options.length > 0) {
      const allCorrect = options.every(o => o.isCorrect);
      if (!allCorrect) {
        // Use functional update to avoid dependency on options array reference
        setOptions(prevOptions => prevOptions.map(o => ({ ...o, isCorrect: true })));
      }
    }
  }, [questionType]); // Only depend on questionType to avoid loops - options are handled in handlers

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingQuestionId ? 'Edit Question' : 'Create Question'}</DialogTitle>
        </DialogHeader>

        {/* Step 1: Question Text and Type */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <Label htmlFor="question-text">Question Text *</Label>
              <Textarea
                id="question-text"
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                placeholder="Enter your question here..."
                className="min-h-[120px] mt-2"
              />
            </div>

            {/* Question Media - Optional */}
            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <ImageIcon size={18} className="text-gray-600" />
                <Label className="text-sm">Add an image to help illustrate this question</Label>
              </div>
              
              <div className="space-y-3">
                <ImageUpload
                  value={mediaItem}
                  onChange={(blobMeta) => {
                    setMediaItem(blobMeta);
                    if (!blobMeta) {
                      setMediaCaption('');
                    }
                  }}
                  acceptedTypes=".jpg,.jpeg,.jfif,.png,.webp"
                  maxBytes={5000000}
                  disabled={false}
                />
                
                {mediaItem && (
                  <div>
                    <Label htmlFor="media-caption">Image Caption (Optional)</Label>
                    <Input
                      id="media-caption"
                      value={mediaCaption}
                      onChange={(e) => setMediaCaption(e.target.value)}
                      placeholder="Enter a caption for the image..."
                      className="mt-2"
                    />
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label>Question Type *</Label>
              <RadioGroup
                value={questionType}
                onValueChange={(value) => {
                  setQuestionType(value as QuestionType);
                  // For specific-string, we'll mark all as correct in useEffect
                  // For other types, clear options
                  if (value !== 'specific-string') {
                    setOptions([]);
                  }
                }}
                className="mt-2 space-y-2"
              >
                {questionTypes.map((type) => (
                  <div key={type.key} className="flex items-center space-x-2">
                    <RadioGroupItem value={type.key} id={`type-${type.key}`} />
                    <Label htmlFor={`type-${type.key}`} className="cursor-pointer">
                      {type.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div>
              <Label htmlFor="points">Points</Label>
              <Input
                id="points"
                type="number"
                min="1"
                max="100"
                value={points}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 1;
                  setPoints(Math.min(100, Math.max(1, value)));
                }}
                className="mt-2"
              />
            </div>

            {timingMode === 'per-question' && (
              <div>
                <Label htmlFor="time-limit">Time Limit (MM:SS) *</Label>
                <Input
                  id="time-limit"
                  type="text"
                  required
                  placeholder="00:00"
                  value={timeLimitDisplay}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTimeLimitDisplay(value);
                    const seconds = mmssToSeconds(value);
                    if (seconds !== null && seconds >= 10) {
                      setTimeLimit(seconds);
                    } else {
                      setTimeLimit(null);
                    }
                  }}
                  onBlur={() => {
                    // Validate and format on blur
                    const seconds = mmssToSeconds(timeLimitDisplay);
                    if (seconds !== null && seconds >= 10) {
                      setTimeLimit(seconds);
                      setTimeLimitDisplay(secondsToMMSS(seconds));
                    } else {
                      setTimeLimitDisplay(secondsToMMSS(timeLimit));
                    }
                  }}
                  className="mt-2"
                  pattern="[0-9]{1,2}:[0-5][0-9]"
                />
                <p className="text-xs text-gray-500 mt-1">Format: MM:SS (e.g., 05:30 for 5 minutes 30 seconds)</p>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Question Options */}
        {step === 2 && (
          <div className="space-y-6">
            {(questionType === 'multiple-choice' || questionType === 'single-selection' || questionType === 'boolean' || questionType === 'specific-string') && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <Label>Answer Options</Label>
                  <Button type="button" variant="outline" size="sm" onClick={handleAddOption}>
                    <Plus size={16} className="mr-1" /> Add Option
                  </Button>
                </div>
                <div className="space-y-3">
                  {options.map((option, index) => (
                    <Card key={option.key} className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-1 space-y-2">
                          <Input
                            value={option.value}
                            onChange={(e) => handleOptionChange(option.key, 'value', e.target.value)}
                            placeholder={`Option ${index + 1}`}
                          />
                          {questionType !== 'specific-string' && (
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={`correct-${option.key}`}
                                checked={option.isCorrect}
                                onCheckedChange={(checked) => handleOptionChange(option.key, 'isCorrect', checked as boolean)}
                                disabled={questionType === 'specific-string'}
                              />
                              <Label htmlFor={`correct-${option.key}`} className="cursor-pointer text-sm">
                                Mark as correct answer
                              </Label>
                            </div>
                          )}
                          {questionType === 'specific-string' && (
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={`correct-${option.key}`}
                                checked={true}
                                disabled={true}
                              />
                              <Label htmlFor={`correct-${option.key}`} className="text-sm text-gray-500">
                                All answers are correct
                              </Label>
                            </div>
                          )}
                        </div>
                        {options.length > 2 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveOption(option.key)}
                          >
                            <Trash2 size={16} className="text-destructive-action" />
                          </Button>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
                {questionType === 'multiple-choice' && (
                  <div className="mt-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="allow-partial"
                        checked={allowPartialCredit}
                        onCheckedChange={(checked) => setAllowPartialCredit(checked as boolean)}
                      />
                      <Label htmlFor="allow-partial" className="cursor-pointer text-sm">
                        Allow partial credit
                      </Label>
                    </div>
                  </div>
                )}
              </div>
            )}

            {questionType === 'freetext' && (
              <div>
                <Label htmlFor="char-limit">Character Limit</Label>
                <Input
                  id="char-limit"
                  type="number"
                  min="1"
                  value={charLimit}
                  onChange={(e) => setCharLimit(parseInt(e.target.value) || 500)}
                  className="mt-2"
                />
                <div className="mt-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="allow-partial-freetext"
                      checked={allowPartialCredit}
                      onCheckedChange={(checked) => setAllowPartialCredit(checked as boolean)}
                    />
                    <Label htmlFor="allow-partial-freetext" className="cursor-pointer text-sm">
                      Allow partial credit
                    </Label>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Time Adjustment (only for adding questions, not editing) */}
        {step === 3 && shouldShowTimeAdjustment && (
          <div className="space-y-6">
            <div className="p-4 bg-action-blue/10 border border-action-blue/30 rounded-lg">
              <label className="flex items-center space-x-2 cursor-pointer mb-3">
                <input
                  type="checkbox"
                  checked={adjustTime}
                  onChange={(e) => {
                    setAdjustTime(e.target.checked);
                    // Reset to calculated value when unchecking
                    if (!e.target.checked) {
                      if (isPerSection && sectionTimeLimit !== undefined && sectionQuestionCount !== undefined) {
                        const currentTime = sectionTimeLimit || 0;
                        if (sectionQuestionCount === 0) {
                          setNewTimeDisplay(secondsToMMSS(DEFAULT_SECONDS_PER_QUESTION));
                        } else {
                          const timePerQuestion = currentTime / sectionQuestionCount;
                          const newTime = currentTime + timePerQuestion;
                          setNewTimeDisplay(secondsToMMSS(Math.max(0, Math.floor(newTime))));
                        }
                      } else if (isPerAssessment && overallTimeLimit !== undefined && totalQuestionCount !== undefined) {
                        const currentTime = overallTimeLimit || 0;
                        if (totalQuestionCount === 0) {
                          setNewTimeDisplay(secondsToMMSS(DEFAULT_SECONDS_PER_QUESTION));
                        } else {
                          const timePerQuestion = currentTime / totalQuestionCount;
                          const newTime = currentTime + timePerQuestion;
                          setNewTimeDisplay(secondsToMMSS(Math.max(0, Math.floor(newTime))));
                        }
                      }
                    }
                  }}
                  className="w-4 h-4 text-action-blue"
                />
                <span className="text-sm font-medium">Increase the time limit accordingly?</span>
              </label>
              
              <div className="ml-6 space-y-3">
                <div className="text-sm text-gray-700">
                  <p className="font-medium mb-2">Calculation:</p>
                  <div className="text-xs font-mono whitespace-pre-line bg-white p-2 rounded border border-gray-200">
                    {getCalculationText()}
                  </div>
                </div>
                <div>
                  <Label htmlFor="new-time" className="text-sm font-medium">
                    New Time Limit (MM:SS):
                  </Label>
                  <Input
                    id="new-time"
                    type="text"
                    placeholder="MM:SS"
                    value={newTimeDisplay}
                    onChange={(e) => setNewTimeDisplay(e.target.value)}
                    onBlur={() => {
                      const seconds = mmssToSeconds(newTimeDisplay);
                      if (seconds === null || seconds < 0 || seconds > 3600) {
                        // Reset to calculated value
                        if (isPerSection && sectionTimeLimit !== undefined && sectionQuestionCount !== undefined) {
                          const currentTime = sectionTimeLimit || 0;
                          if (sectionQuestionCount === 0) {
                            setNewTimeDisplay(secondsToMMSS(DEFAULT_SECONDS_PER_QUESTION));
                          } else {
                            const timePerQuestion = currentTime / sectionQuestionCount;
                            const newTime = currentTime + timePerQuestion;
                            setNewTimeDisplay(secondsToMMSS(Math.max(0, Math.floor(newTime))));
                          }
                        } else if (isPerAssessment && overallTimeLimit !== undefined && totalQuestionCount !== undefined) {
                          const currentTime = overallTimeLimit || 0;
                          if (totalQuestionCount === 0) {
                            setNewTimeDisplay(secondsToMMSS(DEFAULT_SECONDS_PER_QUESTION));
                          } else {
                            const timePerQuestion = currentTime / totalQuestionCount;
                            const newTime = currentTime + timePerQuestion;
                            setNewTimeDisplay(secondsToMMSS(Math.max(0, Math.floor(newTime))));
                          }
                        }
                      } else {
                        setNewTimeDisplay(secondsToMMSS(seconds));
                      }
                    }}
                    className="w-24 mt-1"
                    pattern="[0-9]{1,2}:[0-5][0-9]"
                    disabled={!adjustTime}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Review */}
        {step === 4 && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Review Question</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Question Text</Label>
                  <p className="mt-1 text-sm">{questionText}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Question Type</Label>
                  <p className="mt-1 text-sm">{questionTypes.find(t => t.key === questionType)?.label}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Points</Label>
                  <p className="mt-1 text-sm">{points}</p>
                </div>
                {timeLimit && (
                  <div>
                    <Label className="text-sm font-medium">Time Limit</Label>
                    <p className="mt-1 text-sm">{secondsToMMSS(timeLimit)}</p>
                  </div>
                )}
                {options.length > 0 && (
                  <div>
                    <Label className="text-sm font-medium">Answer Options</Label>
                    <div className="mt-2 space-y-1">
                      {options.map((option, index) => (
                        <div key={option.key} className="text-sm">
                          {index + 1}. {option.value} {option.isCorrect && <span className="text-confirm-action font-semibold">✓ Correct</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {mediaItem && (
                  <div>
                    <Label className="text-sm font-medium">Question Image</Label>
                    <div className="mt-2 space-y-1">
                      <p className="text-sm text-gray-600">Image: {mediaItem.name}</p>
                      {mediaCaption && (
                        <p className="text-sm text-gray-600">Caption: {mediaCaption}</p>
                      )}
                    </div>
                  </div>
                )}
                {shouldShowTimeAdjustment && adjustTime && (
                  <div>
                    <Label className="text-sm font-medium">Time Adjustment</Label>
                    <p className="mt-1 text-sm">Will increase time limit to: {newTimeDisplay}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button variant="outline" onClick={handleBack} disabled={step === 1 || viewState !== viewStates.ready}>
            <ChevronLeft size={16} className="mr-1" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleClose} disabled={viewState === viewStates.adding || viewState === viewStates.updatingTime}>
              Cancel
            </Button>
            {step < 4 ? (
              <Button onClick={handleNext} disabled={!canProceed() || viewState !== viewStates.ready}>
                Next <ChevronRight size={16} className="ml-1" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit} 
                disabled={viewState === viewStates.adding || viewState === viewStates.updatingTime}
              >
                {viewState === viewStates.adding ? 'Adding...' : 
                 viewState === viewStates.updatingTime ? 'Updating...' : 
                 editingQuestionId ? 'Save Question' : 'Create Question'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

